import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { glossary, mobileApp } from '@sorare/core/src/lib/glossary';

import type { Props as PrimaryOfferBuyFieldWrapperProps } from './PrimaryOfferBuyFieldWrapper/index.tsx';
import { PrimaryOfferBuyFieldWrapper } from './PrimaryOfferBuyFieldWrapper/index.tsx';
import type { PrimaryBuyBuyField_anyCard } from './__generated__/index.graphql.ts';

export type Props = Omit<PrimaryOfferBuyFieldWrapperProps, 'children'> &
  Omit<ButtonProps, 'loading'> & { cta?: ReactNode };

export const PrimaryBuyBuyField = ({
  card,
  primaryBuyConfirmationOptions,
  onSuccess,
  onBuyConfirmationClose,
  origin,
  gameSlug,
  showBuyConfirmation = true,
  onClick,
  onlyCreditCards = false,
  applyConversionCredits = true,
  composeSessionId,
  cta,
  rosterId,
  ...props
}: Props) => {
  const ctaNode = ((): ReactNode => {
    if (cta) return cta;

    return <FormattedMessage {...glossary.buyNow} />;
  })();

  if (!card.livePrimaryOffer?.price) return null;

  return (
    <PrimaryOfferBuyFieldWrapper
      card={card}
      primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
      onSuccess={onSuccess}
      onBuyConfirmationClose={onBuyConfirmationClose}
      origin={origin}
      gameSlug={gameSlug}
      showBuyConfirmation={showBuyConfirmation}
      onlyCreditCards={onlyCreditCards}
      applyConversionCredits={applyConversionCredits}
      composeSessionId={composeSessionId}
      onClick={onClick}
      rosterId={rosterId}
    >
      {({ onClick: onBuyClick, loading, disabled }) => {
        if (disabled) {
          return (
            <Button color="primary" {...props} disabled>
              <FormattedMessage {...mobileApp.unavailable} />
            </Button>
          );
        }
        return (
          <LoadingButton
            color="primary"
            onClick={onBuyClick}
            {...props}
            loading={loading}
          >
            {ctaNode}
          </LoadingButton>
        );
      }}
    </PrimaryOfferBuyFieldWrapper>
  );
};

PrimaryBuyBuyField.fragments = {
  anyCard: gql`
    fragment PrimaryBuyBuyField_anyCard on AnyCardInterface {
      slug
      livePrimaryOffer {
        id
        price {
          eurCents
        }
      }
      ...PrimaryOfferBuyFieldWrapper_anyCard
    }
    ${PrimaryOfferBuyFieldWrapper.fragments.anyCard}
  ` as TypedDocumentNode<PrimaryBuyBuyField_anyCard>,
};
